<!-- last modified by AI tools on 2026-10-02 -->
<template>
  <div :class="['market-details-wrapper min-h-screen bg-surface-lowest', { 'hide-scrollbars': !isWeb }]">
    <!-- L1 category nav: sticky to the top (below host chrome). Acts as a global
         category switcher that navigates back to the markets page, filtered. The
         product's own top-level category is highlighted.
         Rendered ONLY in the deep-link ProductDetailsModule mount. When nested
         under MarketsLayout (AppShell / standalone web), the layout owns the
         persistent nav, so we skip it here to avoid a duplicate stacked nav.
         Sits OUTSIDE the .ft2-content cap below so it spans the full width.
         NOTE: this stays OUTSIDE the overflow-clip wrapper below — `position:
         sticky` stops working if any ancestor has overflow other than `visible`
         (including `clip`), because that ancestor becomes the sticky scroll
         context but doesn't actually scroll (the document does). -->
    <div
      v-if="!hasMarketsLayout"
      class="sticky top-[var(--padding-top,0px)] z-30 px-[16px] md:px-[30px] md:mb-2"
      style="background-color: var(--p-content-background); border-bottom: 0.5px solid var(--p-content-border-color)"
    >
      <!-- No active-id: on product details we don't highlight an L1 tab (we're
           viewing a product, not browsing a category). Clicking a tab still
           navigates back to the markets page filtered to that category. -->
      <CategoryNavL1
        class="ft2-content"
        :items="topLevelCategories"
        :active-id="null"
        :include-popular="!isMobile"
        @select="goToCategory"
      />
    </div>

    <!-- Header + content. Capped + centered on web (deep-link mount) via
         .ft2-content; the nav above stays full-width. overflow-x-clip contains
         any horizontal overflow without establishing a scroll container for the
         sticky nav above. -->
    <div :class="['overflow-x-clip', { 'ft2-content': isWeb && !hasMarketsLayout }]">
      <MarketDetailsHeader :mobile="isMobile" />
      <!-- Tab Content -->
      <div :class="isMobile ? 'pb-6' : 'px-6 pb-10'">
        <router-view v-slot="{ Component, route: matchedRoute }">
          <Transition name="content-fade" mode="out-in">
            <component :is="Component" :key="matchedRoute.name" />
          </Transition>
        </router-view>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useMediaQuery } from '@vueuse/core';
import { app } from '@ibgroup-tws-webapps/ratio.ui';
import MarketDetailsHeader from './ProductDetailsHeader.vue';
import CategoryNavL1 from '@/markets/CategoryNavL1.vue';
import { useProductDetails } from '@/stores/useProductDetails';
import { useGetPredictionContractList, useGetProductConditional } from '@/api/prediction.queries';
import { useGetTopLevelCategories } from '@/composables/useCategories';
import { useNavigation } from '@/composables/useNavigation';
import { POPULAR_TAB_ID } from '@/markets/constants';
import { isConditionalProduct } from '@/utils/isConditionalProduct';
import { classifyIntradayContract } from '@/utils/intradayContracts';
import { buildExpirationOptions, expirationOptionKey } from '@/utils/forecastExExpirations';
import { useTargetPricePolling } from '@/composables/useTargetPricePolling';

// Product-details-specific bootstrap. Lives here (not in ProductDetailsModule)
// so it fires in BOTH entry paths:
//   - Host mounts ProductDetailsModule directly via deep link, OR
//   - User lands on '/' (AppShell) and navigates internally to /:conid/product-details/...
// Without this, the AppShell-navigation path renders this layout but never
// updates the productDetails store.
const route = useRoute();

// True when rendered as a nested child of MarketsLayout (AppShell / standalone
// web), where the layout already renders the persistent L1 nav. False (default)
// in the deep-link ProductDetailsModule mount, where this component renders its
// own nav.
const hasMarketsLayout = inject<boolean>('hasMarketsLayout', false);

const moduleProps = inject<{ underlyingConid?: number }>('moduleProps', { underlyingConid: 0 });

const productDetailsStore = useProductDetails();

// L1 category nav: no tab is highlighted here (we're viewing a product, not
// browsing a category); tab clicks navigate to the markets page filtered by
// that category.
const { data: topLevelCategories } = useGetTopLevelCategories();

const { openUrl } = useNavigation();

const goToCategory = (id: string) => {
  // This nav is only rendered in the deep-link ProductDetailsModule mount (when
  // nested under MarketsLayout, the layout owns the nav and this block is
  // hidden). Navigate via the shared hash: openUrl resolves a tws:// module URL
  // internally when the route exists, else hands off to the host's hash router.
  // On narrow viewports the markets page is the mobile AppLanding (no L1 grid
  // filter), so deep-link to the dedicated mobile L2 category route instead.
  // Popular has no category route/param, so fall back to the markets landing.
  if (isMobile.value && id !== POPULAR_TAB_ID) {
    openUrl(`tws://forecast_trader2/category/${id}`);
    return;
  }
  const query = id === POPULAR_TAB_ID ? '' : `?category=${id}`;
  openUrl(`tws://forecast_trader2/${query}`);
};

const resolvedConid = computed(() => {
  const routeConid = Number(route.params.productConid) || 0;
  if (routeConid > 0) return routeConid;
  const moduleConid = moduleProps.underlyingConid ?? 0;
  if (moduleConid > 0) return moduleConid;

  // Deep-link module mounts may receive a concrete router base
  // (`/848767655/product-details`) rather than a `:productConid` param.
  // The route still contains the authoritative conid, so resolve it before
  // deciding whether the contract-list query is enabled.
  const pathConid = route.path?.match(/\/(\d+)\/product-details(?:\/|$)/)?.[1];
  return Number(pathConid) || 0;
});

watch(
  resolvedConid,
  (underlyingConid, previousUnderlyingConid) => {
    // Vue Router reuses this layout when navigation changes only the product conid (including
    // browser Back from a Related Product). The layout therefore does not unmount, and the
    // previous product's measured-period selection would otherwise survive until the Contracts
    // tab remounts. That stale key matches none of the restored product's complex contracts,
    // leaving the contract ladder, chart, and positions empty.
    if (previousUnderlyingConid !== undefined && underlyingConid !== previousUnderlyingConid) {
      productDetailsStore.setSelectedExpiration('');
      productDetailsStore.setExpirationOptions([]);
      productDetailsStore.setSelectedIntradayFlavor(undefined);
      productDetailsStore.setSelectedContractLastTradeTime(0);
      productDetailsStore.setSelectedContractStartTime(0);
    }
    productDetailsStore.setUnderlyingConid(underlyingConid);
  },
  { immediate: true },
);

// Keep product metadata alive at the layout level. Child tabs are keyed and
// unmounted when navigating, so owning this query in ProductContracts made a
// direct refresh on /related start with an empty store and made tab switches
// dependent on whichever child happened to fetch first.
const exchange = computed(() => {
  const routeExchange = route.query.exchange;
  return typeof routeExchange === 'string' ? routeExchange : 'FORECASTX';
});

watch(
  exchange,
  (value) => {
    productDetailsStore.setProductExchange(value);
  },
  { immediate: true },
);

// Single source of truth (set above via underlyingConid + productExchange).
// Child tabs read the same store getter so their query params can't diverge.
const { data: productDetails, refetch: refetchContracts } = useGetPredictionContractList(
  () => productDetailsStore.productQueryParams,
);
const { handlesSelectionRefresh } = useTargetPricePolling(
  () =>
    (productDetails.value?.complex_contracts ?? []).filter(
      (contract) =>
        !productDetailsStore.isConditional || contract.condition_conid === productDetailsStore.selectedConditionConid,
    ),
  () => productDetailsStore.selectedExpiration,
  () => refetchContracts({ cancelRefetch: false }),
);

watch(
  () => productDetailsStore.selectedExpiration,
  (current, previous) => {
    if (current && previous && current !== previous && !handlesSelectionRefresh.value) void refetchContracts();
  },
);

const forecastXProductConid = computed(
  () =>
    productDetails.value?.complex_contracts
      ?.flatMap((contract) => contract.contracts)
      .find((contract) => contract.exchange === 'FORECASTX')?.product_conid ?? 0,
);

const conditionalQueryParams = computed(() => ({
  conid: forecastXProductConid.value,
  exchange: 'FORECASTX',
}));

// Fetch once at the persistent layout level. The Related tab consumes the same
// cached query to render the two component products.
useGetProductConditional(conditionalQueryParams, {
  enabled: computed(() => isConditionalProduct(productDetails.value)),
});

// A deep link to a specific YES/NO contract selects its conditioning group.
// Resolved here, once per target, rather than in the Contracts tab: the tab is
// keyed by route and remounts on every tab switch, and the query data is a new
// reference on every refetch, so a watcher there would keep reverting a
// condition the user picked afterwards.
const deepLinkConid = computed(() => Number(route.query.conid_yes) || Number(route.query.conid_no) || 0);
let appliedDeepLinkConid = 0;

watch(
  [productDetails, deepLinkConid],
  ([details, targetConid]) => {
    productDetailsStore.setProductName(details?.product_name ?? '');
    productDetailsStore.setCategoryId(details?.category_id ?? '');
    productDetailsStore.setTotalOpenInterest(details?.open_interest ?? 0);
    productDetailsStore.setRestricted(!!details?.restricted);
    productDetailsStore.setIsConditional(isConditionalProduct(details));
    productDetailsStore.setConditioningProductConid(details?.condition_conid);
    productDetailsStore.setSettlementProductConid(details?.settlement_conid);
    const conditionalGroups = new Map<number, string>();
    details?.complex_contracts.forEach((contract) => {
      if (contract.condition_conid && contract.condition_label) {
        conditionalGroups.set(contract.condition_conid, contract.condition_label);
      }
    });
    productDetailsStore.setConditionalGroups([...conditionalGroups].map(([conid, label]) => ({ conid, label })));
    productDetailsStore.setForecastXProductConid(forecastXProductConid.value);
    productDetailsStore.setIntradayFlavors(
      (details?.complex_contracts ?? []).flatMap((contract) => {
        const classification = classifyIntradayContract(contract);
        return classification ? [classification.flavor] : [];
      }),
    );

    if (!targetConid || targetConid === appliedDeepLinkConid || !details?.complex_contracts.length) return;
    const match = details.complex_contracts.find((contract) =>
      contract.contracts.some((item) => item.conid_yes === targetConid || item.conid_no === targetConid),
    );
    if (!match) return;
    appliedDeepLinkConid = targetConid;
    if (match.condition_conid) productDetailsStore.setSelectedConditionConid(match.condition_conid);
  },
  { immediate: true },
);

// Badge timing belongs to this persistent layout rather than ProductContracts. The Contracts,
// Related Products, and News tabs are keyed children that unmount when switching tabs, while the
// title dropdown and status badge remain here. Keeping this watcher at the layout level lets a
// selection made on any tab immediately update Live / Closed / Opens soon.
watch(
  [productDetails, () => productDetailsStore.selectedExpiration],
  ([details, selectedKey]) => {
    const complexContracts = details?.complex_contracts;
    if (!selectedKey || !complexContracts?.length) {
      productDetailsStore.setSelectedContractLastTradeTime(0);
      productDetailsStore.setSelectedContractStartTime(0);
      return;
    }

    const selected = buildExpirationOptions(complexContracts).find(
      (option) => expirationOptionKey(option) === selectedKey,
    );
    productDetailsStore.setSelectedContractLastTradeTime(selected?.last_trade_time ?? 0);
    productDetailsStore.setSelectedContractStartTime(selected?.start_trade_time ?? 0);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  productDetailsStore.resetProductDetails();
});

const isMobile = useMediaQuery('(max-width: 767px)');
const isWeb = computed(() => app.option?.clientPlatform === 'web' || app.option?.platform === 'web');

onMounted(() => {
  window.scrollTo(0, 0);
});
</script>
